'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, Loader2, Minus } from 'lucide-react';
import { useSupportChat } from '@/hooks/useSupportChat';
import { createBrowserClient } from '@/lib/supabase';

export function SupportChatWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [chatEnabled, setChatEnabled] = useState<boolean | null>(null);
    const { messages, loading, sending, sendMessage } = useSupportChat();
    const scrollRef = useRef<HTMLDivElement>(null);

    // Check if chat is enabled from support_settings
    useEffect(() => {
        const checkChatEnabled = async () => {
            try {
                const supabase = createBrowserClient();
                const { data, error } = await (supabase
                    .from('support_settings') as any)
                    .select('chat_enabled')
                    .limit(1)
                    .single();

                if (error) {
                    console.error('Error checking chat settings:', error);
                    setChatEnabled(true); // Default to enabled on error
                } else {
                    setChatEnabled(data?.chat_enabled ?? true);
                }
            } catch (err) {
                console.error('Error checking chat settings:', err);
                setChatEnabled(true);
            }
        };

        checkChatEnabled();
    }, []);

    // State for proactive chat from admin
    const [proactiveMessage, setProactiveMessage] = useState<{ message: string; agentName: string } | null>(null);

    // Listen for proactive chat events from admin
    useEffect(() => {
        const handleProactiveChat = (event: CustomEvent<{ message: string; agentName: string }>) => {
            setProactiveMessage(event.detail);
            setIsOpen(true); // Auto-open widget
        };

        window.addEventListener('proactive_chat', handleProactiveChat as EventListener);
        return () => {
            window.removeEventListener('proactive_chat', handleProactiveChat as EventListener);
        };
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!inputValue.trim() || sending) return;
        const msg = inputValue;
        setInputValue('');
        await sendMessage(msg);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Don't render if chat is disabled or still checking
    if (chatEnabled === null || chatEnabled === false) {
        return null;
    }

    return (
        <div className="fixed bottom-24 right-6 lg:bottom-6 z-[100] font-sans">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95, transformOrigin: 'bottom right' }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="absolute bottom-20 right-0 w-[380px] h-[520px] bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                                    <Bot size={20} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-sm">Suporte Atende Turbo</h3>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                        <span className="text-[10px] text-gray-400 font-medium">Equipe Online</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
                            >
                                <Minus size={20} />
                            </button>
                        </div>

                        {/* Messages Area */}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar"
                        >
                            {loading ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-3">
                                    <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
                                    <p className="text-xs">Carregando histórico...</p>
                                </div>
                            ) : messages.length === 0 && !proactiveMessage ? (
                                <div className="h-full flex flex-col items-center justify-center text-center p-6 mt-10">
                                    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                                        <MessageCircle className="w-8 h-8 text-cyan-500/50" />
                                    </div>
                                    <h4 className="text-white font-semibold mb-2">Olá! Como podemos ajudar?</h4>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        Nossa equipe está pronta para tirar suas dúvidas. Envie sua mensagem abaixo.
                                    </p>
                                </div>
                            ) : (
                                messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[85%] rounded-2xl p-3.5 text-sm ${msg.sender_type === 'customer'
                                            ? 'bg-cyan-600 text-white rounded-tr-none shadow-lg shadow-cyan-900/20'
                                            : 'bg-white/10 text-gray-200 border border-white/5 rounded-tl-none'
                                            }`}>
                                            {msg.content}
                                            <p className={`text-[10px] mt-1.5 opacity-50 ${msg.sender_type === 'customer' ? 'text-right' : 'text-left'
                                                }`}>
                                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                            {sending && (
                                <div className="flex justify-end">
                                    <div className="bg-cyan-600/50 text-white/50 rounded-2xl p-3.5 text-sm rounded-tr-none animate-pulse">
                                        Enviando...
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input Area */}
                        <div className="p-4 bg-white/5 border-t border-white/5">
                            <div className="relative flex items-end gap-2 bg-white/5 rounded-2xl border border-white/10 p-2 focus-within:border-cyan-500/50 transition-all">
                                <textarea
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Escreva sua mensagem..."
                                    className="flex-1 bg-transparent border-none focus:ring-0 text-white text-sm py-2 px-3 resize-none max-h-32 min-h-[40px] custom-scrollbar"
                                    rows={1}
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || sending}
                                    className={`p-2.5 rounded-xl transition-all ${inputValue.trim() && !sending
                                        ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30 active:scale-95'
                                        : 'bg-white/5 text-gray-600'
                                        }`}
                                >
                                    <Send size={18} />
                                </button>
                            </div>
                            <p className="text-[10px] text-center text-gray-600 mt-3 font-medium tracking-tight">
                                Chat Atende Turbo
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle Button Container */}
            <div className="relative group">
                {/* CTA Bubble */}
                <AnimatePresence>
                    {!isOpen && (
                        <motion.div
                            initial={{ opacity: 0, x: 20, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 10, scale: 0.9 }}
                            className="absolute bottom-2 right-16 w-max max-w-[200px] bg-white text-gray-900 px-4 py-3 rounded-2xl rounded-br-none shadow-xl border border-white/20 hidden md:block"
                        >
                            <p className="text-xs font-semibold leading-relaxed">
                                Fale conosco agora, tire suas dúvidas aqui 👋
                            </p>
                            <div className="absolute -right-2 bottom-4 w-4 h-4 bg-white transform rotate-45" />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main Toggle Button */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 relative z-10 ${isOpen
                        ? 'bg-white/10 backdrop-blur-md border border-white/20 text-white'
                        : 'bg-gradient-to-br from-cyan-400 to-blue-600 text-white shadow-cyan-500/40'
                        }`}
                >
                    {isOpen ? <X size={24} /> : <MessageCircle size={24} />}

                    {/* Unread Indicator (Optional: Can serve as attention grabber) */}
                    {!isOpen && (
                        <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 border-2 border-[#0A0A0F] rounded-full animate-pulse" />
                    )}
                </motion.button>
            </div>

            <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
      `}</style>
        </div>
    );
}
